import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import Colors from 'pubweb-smokey/dist/colors';
import { modelNameTransformer } from 'pubweb-smokey/dist/utils/modelNameTransformer';
import { LocationsContext } from '@contexts/LocationsContext';
import { DXAccountContext } from '@contexts/DXAccountContext';
import Form from '@components/Shared/Form';
import { runCommonValidation } from 'pubweb-smokey/dist/utils/validation';
import CheckBox from 'pubweb-smokey/dist/components/CheckBox';
import TextField from '@components/Shared/TextField';
import Button from '@components/Shared/Buttons/Button';
import { submitForm } from '@services/formService';
import { getTradeDeskIframe } from '@utils/config';
import LoadingIndicator from '@components/Shared/LoadingIndicator/LoadingIndicator';
import { RequestInfoStyles } from './RequestInfo.styled';
import HomeIllustration from './images/home-illustration.svg';
import { DXFavoritesContext } from '@contexts/DXFavoritesContext';
import { pushGtmFormAction } from '@utils/gtmUtils';

const RequestInfo = ({ homeDetails, onClickOkay, formOpen }) => {
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const accountContext = useContext(DXAccountContext);
  const locationsContext = useContext(LocationsContext);
  const favoriteContext = useContext(DXFavoritesContext);

  // We want form_submit_text parameter to match the submit button text. Added this const so the two would stay in sync if text was changed.
  const submitButtonText = 'Request Info';

  const validateForm = (formObj) => {
    var formErrors = runCommonValidation(formObj);
    setFormErrors(formErrors);
    return formErrors.hasErrors;
  };

  const formObj = {};

  const requestInfo = (formObj) => {
    if (formSubmitting || formSubmitted) {
      return;
    }

    setFormSubmitting(true);
    formObj.modelNumber = homeDetails.modelNumber;
    formObj.canEmail = true;
    formObj.originWebFormType = 'lead-form';
    formObj.originWebApp = 'clayton-homes-website';
    formObj.originWebPageType = 'clayton-homes-home-detail';
    formObj.originWebFormCategory = 'home-details';
    formObj.dataExtension = 'FormActivityCH';

    submitForm(formObj).then(() => {
      pushGtmFormAction({
        form_action_type: 'submit',
        model_number: homeDetails?.modelNumber,
        form_name: 'home_details',
        form_method: 'embedded',
        form_id: 'home_details_submit',
        form_submit_text: submitButtonText
          .trim()
          .toLowerCase()
          .replaceAll(' ', '_'),
      });

      if (typeof window !== 'undefined') {
        window.analytics.identify(accountContext.state?.account?.userId, {
          firstName:
            accountContext.state.account?.firstName || formObj.firstName,
          lastName: accountContext.state.account?.lastName || formObj.lastName,
          phone: accountContext.state.account?.phone || formObj.phone,
          email: accountContext.state?.email || formObj.email,
        });
      }

      setFormSubmitted(true);
      setFormSubmitting(false);
    });
  };

  useEffect(() => {
    if (formOpen) {
      document.getElementById('firstName').focus();
    }
  }, [formOpen]);

  const portalLogin = () => {
    onClickOkay();
    accountContext.actions.navigateToPortal('/portal/');
  };

  const viewInFavorites = () => {
    onClickOkay();
    accountContext.actions.navigateToPortal('/portal/favorites/');
  };

  const addToFavorites = () => {
    favoriteContext.actions.toggleFavoriteModel(homeDetails?.modelNumber);
    onClickOkay();
    setTimeout(() => {
      accountContext.actions.navigateToPortal('/portal/favorites/');
    }, 1000);
  };

  return (
    <RequestInfoStyles>
      <div className="request-info-container">
        <p className="label">requesting information</p>
        <h3>{modelNameTransformer(homeDetails.modelDescription)}</h3>

        {!formSubmitted && (
          <div>
            <Form onFormValidate={validateForm} onSubmit={requestInfo}>
              <div className="form-row form-row-name">
                <TextField
                  id="firstName"
                  defaultValue={
                    accountContext.state.account?.firstName || formObj.firstName
                  }
                  errorMessage={formErrors.firstName}
                  textFieldStyle="primary"
                  label="First name"
                  automationId="request-info-first-name-textfield"
                />
                <TextField
                  id="lastName"
                  defaultValue={
                    accountContext.state.account?.lastName || formObj.lastName
                  }
                  errorMessage={formErrors.lastName}
                  textFieldStyle="primary"
                  label="Last name"
                  automationId="request-info-last-name-textfield"
                />
              </div>

              <div className="form-row">
                <TextField
                  id="phone"
                  defaultValue={
                    accountContext.state.account?.phone || formObj.phone
                  }
                  errorMessage={formErrors.phone}
                  textFieldStyle="primary"
                  label="Phone number"
                  automationId="request-info-phone-textfield"
                />
              </div>

              <div className="form-row">
                <TextField
                  id="email"
                  defaultValue={accountContext.state.email || formObj.email}
                  errorMessage={formErrors.email}
                  textFieldStyle="primary"
                  label="Email"
                  automationId="request-info-email-textfield"
                />
              </div>
              <div className="form-row">
                <TextField
                  id="zip"
                  defaultValue={
                    accountContext.state.account?.zipcode ||
                    locationsContext.state.postalCode
                  }
                  errorMessage={formErrors.zip}
                  textFieldStyle="primary"
                  label="Zip"
                  automationId="request-info-zip-textfield"
                />
              </div>
              <div className="form-row">
                <CheckBox
                  id="writtenTextMessageConsent"
                  className="written-text-check"
                  automationId="written-text-consent-checkbox"
                >
                  Please text me (Optional)
                </CheckBox>
                <p className="sms-disclaimer">
                  By selecting “Text”, you agree that we may, but are not
                  obligated to, provide communications and transaction documents
                  to you via text message. In addition, you acknowledge that you
                  are able to access the communications and transaction
                  documents provided to you by text message. Consent not
                  required for purchase. Msg & data rates may apply. See our
                  Communications Terms and Conditions at&nbsp;
                  <Link href="/legal" target="_blank">
                    Legal | Clayton Homes
                  </Link>
                  , which includes opt-out instructions.
                </p>
              </div>
              <Button
                className="gtm-request-info-submit-btn"
                aria-label="Request Info"
                automationId="home-details-request-info"
                fillContainer={true}
                disabled={formSubmitting}
                type="submit"
              >
                {!formSubmitting ? (
                  submitButtonText
                ) : (
                  <LoadingIndicator
                    color={Colors.primary.white.standard}
                    width={24}
                    height={24}
                  />
                )}
              </Button>
            </Form>
            <p className="disclaimer xsmall">
              By entering your email address, you agree to receive marketing
              emails from Clayton. Unsubscribe any time.
            </p>
          </div>
        )}

        {formSubmitted && (
          <div>
            <div className="illustration">
              <HomeIllustration />
            </div>
            <p className="thank-you-text intro-paragraph">
              Thank you for reaching out. A home center consultant will be in
              touch shortly.
            </p>
            {accountContext.state?.account ? (
              favoriteContext.state.favoriteModels?.filter(
                (faveModels) =>
                  faveModels.modelNumber === homeDetails.modelNumber
              )[0] ? (
                <Button
                  aria-label="view in favorites"
                  fillContainer={true}
                  onClick={viewInFavorites}
                >
                  View In Favorites
                </Button>
              ) : (
                <Button
                  aria-label="add to favorites"
                  fillContainer={true}
                  onClick={addToFavorites}
                >
                  Add to Favorites ❤
                </Button>
              )
            ) : (
              <Button
                aria-label="myhome account"
                fillContainer={true}
                onClick={portalLogin}
              >
                Log In
              </Button>
            )}
            {getTradeDeskIframe()}
          </div>
        )}
      </div>
    </RequestInfoStyles>
  );
};

RequestInfo.propTypes = {
  homeDetails: PropTypes.object.isRequired,
  onClickOkay: PropTypes.func.isRequired,
  formOpen: PropTypes.bool.isRequired,
};

RequestInfo.propTypes = {
  homeDetails: PropTypes.shape({
    modelDescription: PropTypes.any,
    modelId: PropTypes.any,
    modelNumber: PropTypes.any,
  }),
};

export default RequestInfo;
